import React from 'react';
import { ArrowRight, CalendarClock, CheckCircle2 } from 'lucide-react';

import { tr } from '../../i18n/text';
import { databaseService } from '../../services/database';
import { trackCtaClick, trackLead, trackEvent } from '../../services/analytics';
import { useLeadGuard } from '../../hooks/useLeadGuard';

/**
 * Inline lead form for high-intent pages. All variants post to submitContactForm with their own
 * inquiryType, so the lead lands in the super-admin Leads inbox with its source / country /
 * language (added by databaseService) and the honeypot check.
 *
 *   migration  — /alternatives/*, /compare: "switching from X" with a client-count picker
 *   call       — country + feature pages: talk to us / book a call
 *   calculator — cost calculator: send me this comparison + a switch-over plan
 *
 * The `call` variant renders on translated pages (/hi /ar /es feature pages), so every string in
 * it goes through tr() and must exist in src/i18n/dict (the prerender fails otherwise).
 */

export const BOOKING_URL = 'https://cal.id/trainzilla-sales';

type Variant = 'migration' | 'call' | 'calculator';

interface Props {
  variant: Variant;
  /** Stable page id for analytics + the lead subject, e.g. "alternatives-trainerize", "country-uk". */
  context: string;
  /** Competitor name for the migration variant. */
  competitorName?: string;
  /** Extra text appended to the lead message (e.g. the calculator's comparison). */
  summary?: string;
  className?: string;
}

const CLIENT_COUNTS = ['1–10', '11–25', '26–50', '51–100', '100+'];

const card: React.CSSProperties = { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' };
const field: React.CSSProperties = {
  backgroundColor: 'var(--bg-primary)',
  border: '1px solid var(--border-color)',
  color: 'var(--text-primary)',
};
const fieldClass = 'w-full rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500';
const gradient: React.CSSProperties = { background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' };

const LeadCaptureCard: React.FC<Props> = ({ variant, context, competitorName, summary, className = '' }) => {
  const guard = useLeadGuard();
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [clients, setClients] = React.useState('');
  const [note, setNote] = React.useState('');
  const [state, setState] = React.useState<'idle' | 'sending' | 'done'>('idle');
  const [error, setError] = React.useState('');
  const startedRef = React.useRef(false);

  const copy = {
    migration: {
      title: `Switching from ${competitorName ?? 'another app'}? We'll help you move.`,
      body: `Tell us roughly how many clients you have. We'll reply within one business day with how to bring them over (Coach Pro has a bulk CSV import) and, if you like, a free 20-minute switch-over call with the founder.`,
      notePlaceholder: 'Anything tricky to move? e.g. programs, payments, check-in forms',
      button: 'Get my switch-over plan',
      subject: `Switching from ${competitorName ?? 'another app'}`,
    },
    calculator: {
      title: 'Want this comparison and a switch-over plan by email?',
      body: "We'll send it within one business day, with how to move your clients over. No newsletter, no spam.",
      notePlaceholder: 'Anything else we should know? (optional)',
      button: 'Email me this',
      subject: 'Cost calculator: send my comparison',
    },
    call: {
      title: tr('Questions before you switch?'),
      body: tr("Leave your email and we'll reply within one business day, or book a free 20-minute call with the founder at a time that suits your time zone."),
      notePlaceholder: tr('What would you like to know? (optional)'),
      button: tr('Send'),
      subject: `Question from ${context}`,
    },
  }[variant];

  const onFirstInput = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackEvent('form_start', { form_id: `lead_${variant}`, context });
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError(tr('Please add your name and email.'));
      return;
    }
    setState('sending');
    setError('');
    const lines = [
      variant === 'migration' && competitorName ? `Switching from: ${competitorName}` : '',
      clients ? `Clients: ${clients}` : '',
      note.trim(),
      summary ? `\n${summary}` : '',
    ].filter(Boolean);
    const result = await databaseService.submitContactForm({
      full_name: name.trim(),
      email: email.trim(),
      inquiry_type: variant === 'call' ? 'call_request' : variant,
      subject: copy.subject,
      message: lines.join('\n') || copy.subject,
      ...guard.read(),
    });
    if (result.success) {
      setState('done');
      trackLead({ form_id: `lead_${variant}`, method: 'inline_card', context });
    } else {
      setState('idle');
      setError(result.error || tr('Something went wrong. Please try again.'));
    }
  };

  const bookingLink = (
    <a
      href={BOOKING_URL}
      target="_blank"
      rel="noopener"
      onClick={() => trackCtaClick('book_call', { context, variant })}
      className="inline-flex items-center gap-2 text-sm font-semibold"
      style={{ color: 'var(--accent-indigo)' }}
    >
      <CalendarClock className="w-4 h-4" /> {tr('Book a free 20-minute call')}
    </a>
  );

  return (
    <section className={`p-7 rounded-2xl ${className}`} style={card} aria-label={copy.title}>
      {state === 'done' ? (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2 text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
            <CheckCircle2 className="w-5 h-5" style={{ color: 'var(--accent-emerald)' }} />
            {tr('Thanks, we got it.')}
          </div>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {tr("We'll email you within one business day. Want to pick a time now instead?")}
          </p>
          {bookingLink}
        </div>
      ) : (
        <>
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{copy.title}</h2>
          <p className="text-sm leading-relaxed mb-5" style={{ color: 'var(--text-secondary)' }}>{copy.body}</p>
          <form onSubmit={onSubmit} onInput={onFirstInput} className="relative grid grid-cols-1 sm:grid-cols-2 gap-3" noValidate>
            {guard.field}
            <label className="sr-only" htmlFor={`lc-name-${context}`}>{tr('Your name')}</label>
            <input
              id={`lc-name-${context}`}
              className={fieldClass}
              style={field}
              placeholder={tr('Your name')}
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <label className="sr-only" htmlFor={`lc-email-${context}`}>{tr('Email')}</label>
            <input
              id={`lc-email-${context}`}
              className={fieldClass}
              style={field}
              type="email"
              placeholder={tr('Email')}
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {variant === 'migration' && (
              <>
                <label className="sr-only" htmlFor={`lc-clients-${context}`}>How many clients do you coach?</label>
                <select
                  id={`lc-clients-${context}`}
                  className={fieldClass}
                  style={field}
                  value={clients}
                  onChange={(e) => setClients(e.target.value)}
                >
                  <option value="">How many clients do you coach?</option>
                  {CLIENT_COUNTS.map((c) => (
                    <option key={c} value={c}>{c} clients</option>
                  ))}
                </select>
              </>
            )}
            <label className="sr-only" htmlFor={`lc-note-${context}`}>{copy.notePlaceholder}</label>
            <input
              id={`lc-note-${context}`}
              className={`${fieldClass} ${variant !== 'migration' ? 'sm:col-span-2' : ''}`}
              style={field}
              placeholder={copy.notePlaceholder}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
            />
            <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 mt-1">
              <button
                type="submit"
                disabled={state === 'sending'}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-white disabled:opacity-60"
                style={gradient}
              >
                {state === 'sending' ? tr('Sending…') : copy.button} <ArrowRight className="w-4 h-4" />
              </button>
              {bookingLink}
            </div>
            {error && (
              <p className="sm:col-span-2 text-sm" role="alert" style={{ color: 'var(--accent-red, #ef4444)' }}>{error}</p>
            )}
          </form>
        </>
      )}
    </section>
  );
};

export default LeadCaptureCard;
